import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Receipt, Search, Plus, Eye, Printer, RotateCcw, BadgeCheck, Clock, FileWarning, CircleDollarSign,
} from "lucide-react";
import { toast } from "sonner";
import { useQueueAnnouncements, useSoundArm, ACCEPT_CASHIER, playAnnouncement } from "@/lib/announcer";
import { BellRing } from "lucide-react";

const PESO = "\u20B1";
const STATUSES = ["all", "draft", "waiting_payment", "paid", "voided"];
const STATUS_LABEL = {
  draft: "Draft",
  waiting_payment: "Waiting Payment",
  paid: "Paid",
  voided: "Voided",
};
const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  waiting_payment: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  voided: "bg-rose-50 text-rose-700 border-rose-200",
};

function fmtMoney(n) {
  const v = Number(n || 0);
  return `${PESO}${v.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

export default function POS() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;
  const isAdminLike = role === "admin" || role === "hospital_admin";
  const isDoctor = role === "doctor";

  const [scope, setScope] = useState(isDoctor ? "mine" : "all"); // doctor always mine; admin can toggle
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Quick-create dialog state
  const [patients, setPatients] = useState([]);
  const [patientQuery, setPatientQuery] = useState("");
  const [creating, setCreating] = useState(false);

  // === Cashier station: billing-lane queue + call-outs ===
  const [billingQueue, setBillingQueue] = useState([]);
  const { armed: soundOn, arm: armSound } = useSoundArm();
  const { markSelfPlayed } = useQueueAnnouncements({ accept: ACCEPT_CASHIER, enabled: soundOn });

  const loadBillingQueue = async () => {
    try {
      const { data } = await api.get("/queue/today");
      const rows = Array.isArray(data) ? data : (data?.items || []);
      setBillingQueue(rows.filter((q) => q.status === "billing"));
    } catch { /* queue module may be off for this clinic */ }
  };

  useEffect(() => {
    loadBillingQueue();
    const t = setInterval(loadBillingQueue, 10000);
    return () => clearInterval(t);
  }, []);

  const callToCashier = async (q) => {
    try {
      const { data } = await api.post(`/queue/${q.id}/announce`, { call_type: "cashier" });
      markSelfPlayed(data.id);
      await playAnnouncement(data);
      toast.success(`Calling ${q.patient_snapshot?.full_name || "patient"} to the cashier`);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { scope };
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.q = search;
      const [tx, sm] = await Promise.all([
        api.get("/pos/transactions", { params }),
        api.get("/pos/transactions/summary", { params: { scope } }),
      ]);
      setTransactions(tx.data || []);
      setSummary(sm.data || null);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/patients").then((r) => setPatients(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, statusFilter]);

  const filteredPatients = useMemo(() => {
    const q = patientQuery.toLowerCase().trim();
    if (!q) return patients.slice(0, 8);
    return patients.filter((p) =>
      (p.full_name || "").toLowerCase().includes(q) ||
      (p.contact_number || "").toLowerCase().includes(q)
    ).slice(0, 8);
  }, [patients, patientQuery]);

  const createForPatient = async (patient) => {
    setCreating(true);
    try {
      // Preferred flow:
      // Patient selected -> latest RX -> POS from prescription.
      // This auto-adds Consultation Fee and prescribed medicines.
      try {
        const lastRx = await api.get(`/prescriptions/last/patient/${patient.id}`);
        if (lastRx?.data?.id) {
          const res = await api.post(`/pos/from-prescription/${lastRx.data.id}`);
          const medCount = Array.isArray(res.data?.line_items)
            ? res.data.line_items.filter((li) => li.item_type === "medicine").length
            : 0;
          toast.success(
            medCount > 0
              ? `Created ${res.data.transaction_number} from latest RX with ${medCount} medicine(s)`
              : `Created ${res.data.transaction_number} from latest RX`
          );
          navigate(`/pos/transactions/${res.data.id}`);
          return;
        }
      } catch (rxErr) {
        // No prior RX for this patient, or RX conversion unavailable.
        // Fall back to blank POS so billing can still proceed.
      }

      const res = await api.post("/pos/transactions", {
        patient_id: patient.id,
        doctor_id: isDoctor ? user.id : "",
        line_items: [],
        status: "draft",
      });
      toast.success(`Created blank ${res.data.transaction_number} — no previous RX found`);
      navigate(`/pos/transactions/${res.data.id}`);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  // Walk-in (non-patient) sale: someone just buying medicine, no patient record.
  const createWalkIn = async () => {
    const name = (window.prompt("Walk-in customer name (optional):", "") || "").trim();
    setCreating(true);
    try {
      const res = await api.post("/pos/transactions", {
        patient_id: "",
        customer_name: name,
        doctor_id: "",
        line_items: [],
        status: "draft",
      });
      toast.success(`Walk-in sale ${res.data.transaction_number} started`);
      navigate(`/pos/transactions/${res.data.id}`);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const counts = summary?.counts || { draft: 0, waiting_payment: 0, paid: 0, voided: 0 };

  return (
    <div className="p-5 lg:p-7 space-y-5" data-testid="pos-page">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Receipt size={22} className="text-blue-600" /> POS / Billing
          </h1>
          <p className="text-sm text-slate-600">
            Manage bills, payments, and receipts. Currency: ₱ PHP. Tax: 0%.
          </p>
        </div>
        <div className="flex gap-2">
          {isAdminLike ? (
            <>
              <Button variant="outline" onClick={() => navigate("/pos/services")} data-testid="btn-pos-services">
                Services
              </Button>
              <Button variant="outline" onClick={() => navigate("/pos/reports")} data-testid="btn-pos-reports">
                Reports
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="pos-counters">
        <Card className="p-4" data-testid="counter-draft">
          <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Clock size={12} /> Draft</div>
          <div className="text-2xl font-semibold text-slate-900 mt-1">{counts.draft || 0}</div>
        </Card>
        <Card className="p-4" data-testid="counter-waiting">
          <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><CircleDollarSign size={12} /> Waiting Payment</div>
          <div className="text-2xl font-semibold text-amber-700 mt-1">{counts.waiting_payment || 0}</div>
        </Card>
        <Card className="p-4" data-testid="counter-paid">
          <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><BadgeCheck size={12} /> Paid</div>
          <div className="text-2xl font-semibold text-emerald-700 mt-1">{counts.paid || 0}</div>
        </Card>
        <Card className="p-4" data-testid="counter-voided">
          <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><FileWarning size={12} /> Voided</div>
          <div className="text-2xl font-semibold text-rose-700 mt-1">{counts.voided || 0}</div>
        </Card>
        <Card className="p-4 col-span-2 md:col-span-1" data-testid="counter-today-collected">
          <div className="text-xs uppercase tracking-wider text-slate-500">Today's Collection</div>
          <div className="text-xl font-bold text-blue-700 mt-1">{fmtMoney(summary?.today_paid_total)}</div>
          <div className="text-xs text-slate-500">{summary?.today_paid_count || 0} paid txns</div>
        </Card>
      </div>

      {/* Cashier station: patients sent to billing, callable from here */}
      <Card className="p-5 space-y-3" data-testid="pos-cashier-queue">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Receipt size={14} /> Cashier Queue
            <span className="text-xs font-normal text-slate-500">({billingQueue.length} waiting to pay)</span>
          </div>
          <Button
            size="sm"
            variant={soundOn ? "outline" : "default"}
            onClick={armSound}
            disabled={soundOn}
            className={soundOn ? "" : "bg-amber-600 hover:bg-amber-700 text-white"}
            data-testid="btn-enable-sound"
            title="Play call-outs on this computer (cashier + doctor calls)"
          >
            <BellRing size={14} className="mr-1" />
            {soundOn ? "Sound On" : "Enable Sound"}
          </Button>
        </div>

        {billingQueue.length === 0 ? (
          <div className="text-sm text-slate-400 py-2">No patients sent to the cashier right now.</div>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
            {billingQueue.map((q) => (
              <div key={q.id} className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`cashier-row-${q.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{q.patient_snapshot?.full_name || "—"}</div>
                  <div className="text-xs text-slate-500">
                    {q.queue_number ? `Queue #${q.queue_number} · ` : ""}{q.assigned_doctor_name || ""}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => callToCashier(q)} data-testid={`cashier-call-${q.id}`}>
                    <BellRing size={14} className="mr-1 text-amber-600" /> Call to Cashier
                  </Button>
                  {q.patient_id ? (
                    <Button size="sm" variant="outline" onClick={() => createForPatient({ id: q.patient_id })} disabled={creating} data-testid={`cashier-bill-${q.id}`}>
                      <Receipt size={14} className="mr-1 text-emerald-600" /> Bill
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Quick-create card */}
      <Card className="p-5 space-y-3" data-testid="pos-quick-create">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Plus size={14} /> New POS Transaction
          </div>
          <Button size="sm" variant="outline" onClick={createWalkIn} disabled={creating} data-testid="pos-walk-in" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            <Receipt size={14} className="mr-1" /> Walk-in Sale (no patient)
          </Button>
        </div>
        <div className="text-xs text-slate-500">Search a patient below, or start a <b>Walk-in Sale</b> for a non-patient just buying medicine.</div>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 text-slate-400" size={14} />
          <Input
            className="pl-9"
            placeholder="Search patient by name or contact..."
            value={patientQuery}
            onChange={(e) => setPatientQuery(e.target.value)}
            data-testid="pos-patient-search"
          />
          {patientQuery && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border rounded-md shadow-sm max-h-56 overflow-auto divide-y" data-testid="pos-patient-results">
              {filteredPatients.length === 0 && (
                <div className="p-3 text-sm text-slate-500">No patients found</div>
              )}
              {filteredPatients.map((p) => (
                <button
                  key={p.id}
                  disabled={creating}
                  onClick={() => createForPatient(p)}
                  className="w-full text-left p-2 hover:bg-blue-50 disabled:opacity-50"
                  data-testid={`pos-quick-create-${p.id}`}
                >
                  <div className="text-sm font-medium text-slate-900">{p.full_name}</div>
                  <div className="text-xs text-slate-500">
                    {p.age ? `${p.age} y/o` : ""} {p.gender || ""} {p.contact_number ? ` · ${p.contact_number}` : ""}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Filter + List */}
      <Card className="p-0 overflow-hidden" data-testid="pos-list-card">
        <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`filter-status-${s}`}>
                    {s === "all" ? "All statuses" : STATUS_LABEL[s] || s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdminLike ? (
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="h-8 w-52 text-xs" data-testid="filter-scope">
                  <SelectValue placeholder="Scope" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="filter-scope-all">All Hospital Transactions</SelectItem>
                  <SelectItem value="mine" data-testid="filter-scope-mine">My Transactions</SelectItem>
                </SelectContent>
              </Select>
            ) : null}
            <Input
              className="h-8 w-56 text-xs"
              placeholder="Search invoice or patient..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              data-testid="filter-search"
            />
            <Button variant="outline" size="sm" onClick={load} data-testid="btn-refresh">Refresh</Button>
          </div>
          <div className="text-xs text-slate-500">{transactions.length} transactions</div>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm" data-testid="pos-tx-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Invoice #</th>
                <th className="text-left px-4 py-2 font-medium">Patient</th>
                <th className="text-left px-4 py-2 font-medium">Doctor</th>
                <th className="text-right px-4 py-2 font-medium">Total</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Created</th>
                <th className="text-right px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={7} className="text-center text-slate-500 py-8">Loading…</td></tr>
              ) : transactions.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-slate-500 py-8">No transactions yet. Create one above.</td></tr>
              ) : (
                transactions.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50" data-testid={`pos-row-${t.id}`}>
                    <td className="px-4 py-2 font-medium text-slate-900">{t.transaction_number}</td>
                    <td className="px-4 py-2">{t.patient_snapshot?.full_name || "—"}</td>
                    <td className="px-4 py-2 text-slate-600">{t.doctor_snapshot?.name || "—"}</td>
                    <td className="px-4 py-2 text-right font-medium">{fmtMoney(t.grand_total)}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className={STATUS_STYLE[t.status] || ""}>
                        {STATUS_LABEL[t.status] || t.status}
                      </Badge>
                      {t.discount_approval_required ? (
                        <span className="ml-2 text-[10px] uppercase text-rose-700 font-semibold">Approval needed</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(t.created_at)}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/pos/transactions/${t.id}`)} data-testid={`btn-open-${t.id}`}>
                          <Eye size={12} className="mr-1" /> Open
                        </Button>
                        {t.status === "paid" ? (
                          <Button size="sm" variant="outline" onClick={() => navigate(`/pos/transactions/${t.id}?print=1`)} data-testid={`btn-print-${t.id}`}>
                            <Printer size={12} className="mr-1" /> Print
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
